import { useEffect, useState } from 'react';

import {
  CloseCircleOutlined,
  WarningOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { Image } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaStopPhoto } from 'models/TiendaPerfecta';

const I18N_BASE = 'scenes.route_report.components.stop_activity';

interface StopPhotoGridProps {
  photos: TiendaPerfectaStopPhoto[];
}

/**
 * Las fotos de un paso o de una incidencia.
 *
 * Es lo ÚNICO de esta pantalla que sigue cargando después de la respuesta:
 * `/stops/{id}` contesta el payload entero de una vez, pero cada foto es una
 * descarga aparte contra S3. Por eso cada celda reserva su cuadrado antes de
 * que la imagen exista —el `aspect-ratio` del contenedor no depende de la
 * descarga— y muestra su propio bloque de carga mientras tanto: sin eso la
 * grilla crece de golpe al entrar la primera imagen y el modal salta con ella.
 *
 * Las URLs se firman en CADA petición y caducan en una hora, así que nunca hay
 * nada cacheado que mostrar de entrada.
 */
function StopPhotoGrid({ photos }: StopPhotoGridProps) {
  const { t } = useTranslation();

  /**
   * Fotos con URL firmada que aun así no cargaron.
   *
   * Que el backend emita la URL no garantiza que el archivo esté: firmar no
   * verifica existencia, así que un objeto borrado o nunca subido produce una
   * URL válida que devuelve error. Pasa de verdad en este módulo. Sin esto la
   * celda queda con el icono de imagen rota del navegador y el `alt` a la
   * vista, que no le dice nada a nadie.
   */
  const [failed, setFailed] = useState<number[]>([]);

  /* Las URLs se re-firman en cada apertura: al cambiar el juego de fotos hay
     que volver a intentarlas todas, o una que falló una vez quedaría marcada
     para siempre.

     La dependencia es una CLAVE derivada del contenido y no el arreglo: un
     arreglo depende de su identidad, así que a un llamador que pase una
     expresión en línea —`photos={x ?? []}`, un `.filter(...)`— este efecto le
     correría en cada render y lo dejaría en un bucle. */
  const photoKey = photos.map((photo) => photo.url ?? '').join('|');

  useEffect(() => setFailed([]), [photoKey]);

  if (!photos?.length) return null;

  const markFailed = (position: number) =>
    setFailed((previous) =>
      previous.includes(position) ? previous : [...previous, position]
    );

  return (
    <div className="stop-photos">
      <Image.PreviewGroup>
        {photos.map((photo) => {
          /* Dos fallas distintas, y la diferencia le importa a quien mira:
             sin URL la firma no se pudo emitir AHORA y volver a abrir el
             detalle probablemente la resuelva —es amarillo, una advertencia—;
             con URL que no carga, el archivo no está donde debería, y eso no se
             arregla reintentando —es rojo, un error—.

             Lo que ninguna de las dos significa es "no hay foto": `present` es
             `true` en ambos casos y la tripulación sí dejó esa evidencia. */
          if (!photo.url) {
            return (
              <div
                className="stop-photo stop-photo--unavailable"
                key={photo.position}
              >
                <WarningOutlined />
                <span>{t(`${I18N_BASE}.photo_unavailable`)}</span>
              </div>
            );
          }

          if (failed.includes(photo.position)) {
            return (
              <div
                className="stop-photo stop-photo--broken"
                key={photo.position}
              >
                <CloseCircleOutlined />
                <span>{t(`${I18N_BASE}.photo_broken`)}</span>
                <span className="stop-photo__badge">#{photo.position}</span>
              </div>
            );
          }

          return (
            <div className="stop-photo" key={photo.position}>
              <Image
                src={photo.url as string}
                alt={t(`${I18N_BASE}.photo_position`, {
                  position: photo.position,
                })}
                width="100%"
                height="100%"
                style={{ objectFit: 'cover' }}
                /* El bloque de carga ocupa exactamente el cuadrado de la celda,
                   así que la imagen lo reemplaza sin mover nada. */
                placeholder={<div className="stop-photo__loading" />}
                onError={() => markFailed(photo.position)}
                preview={{
                  mask: (
                    <span className="stop-photo__mask">
                      <ZoomInOutlined />
                    </span>
                  ),
                }}
              />
              <span className="stop-photo__badge">#{photo.position}</span>
            </div>
          );
        })}
      </Image.PreviewGroup>
    </div>
  );
}

export default StopPhotoGrid;
